import { useMemo, useSyncExternalStore } from "react";
import { vendorStore } from "./index";
import type { Vendor, VendorStore } from "./types";
import { useExpenses } from "../expenses/useExpenses";
import { useInvoices } from "../invoices/useInvoices";

function getSnapshot(): Vendor[] {
  return vendorStore.list();
}

const SERVER_SNAPSHOT: Vendor[] = [];
function getServerSnapshot(): Vendor[] {
  return SERVER_SNAPSHOT;
}

function subscribe(listener: () => void): () => void {
  return vendorStore.subscribe(listener);
}

export interface UseVendorsResult {
  vendors: Vendor[];
  getById(id: string): Vendor | undefined;
  upsertByName: VendorStore["upsertByName"];
  update: VendorStore["update"];
  remove: VendorStore["remove"];
  merge: VendorStore["merge"];
}

export function useVendors(): UseVendorsResult {
  const vendors = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  );
  return {
    vendors,
    getById: (id: string) => vendors.find((v) => v.id === id),
    upsertByName: vendorStore.upsertByName.bind(vendorStore),
    update: vendorStore.update.bind(vendorStore),
    remove: vendorStore.remove.bind(vendorStore),
    merge: vendorStore.merge.bind(vendorStore),
  };
}

export interface VendorStats {
  totalCents: number;
  txnCount: number;
  lastActivityIso: string | null;
}

// Derive aggregate stats for a single vendor from the already-loaded expense
// and invoice snapshots. Computing client-side keeps us off a separate RPC
// and stays live as new rows arrive. For the list page, call this once per
// row — the Map lookups inside `useVendors*` are O(n) per render but the
// vendor counts are small enough that perf isn't a concern for Wave 9.
export function useVendorStats(vendorId: string): VendorStats {
  const { expenses } = useExpenses();
  const { invoices } = useInvoices();

  return useMemo<VendorStats>(() => {
    let totalCents = 0;
    let txnCount = 0;
    let lastActivityIso: string | null = null;

    const bumpActivity = (iso: string | null) => {
      if (!iso) return;
      if (!lastActivityIso || iso > lastActivityIso) lastActivityIso = iso;
    };

    for (const e of expenses) {
      if (e.vendorId !== vendorId) continue;
      totalCents += e.amountCents;
      txnCount += 1;
      bumpActivity(e.occurredOn);
    }
    for (const i of invoices) {
      if (i.vendorId !== vendorId) continue;
      totalCents += i.amountCents;
      txnCount += 1;
      bumpActivity(i.issuedOn);
    }

    return { totalCents, txnCount, lastActivityIso };
  }, [expenses, invoices, vendorId]);
}
